import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, MessageCircle, Mail, HelpCircle, Truck, ShieldCheck, Sparkles, Zap, Package, Wrench, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useSiteBrand } from '../components/BrandLogo';

const CATEGORIES = [
  { id: 'all', label: 'Todo', icon: HelpCircle },
  { id: 'envios', label: 'Envíos', icon: Truck },
  { id: 'producto', label: 'Producto', icon: Package },
  { id: 'garantia', label: 'Garantía', icon: ShieldCheck },
];

const FAQS = [
  {
    id: 'faq-1',
    cat: 'envios',
    q: '¿Puedo acudir a sus instalaciones a recoger el producto?',
    a: (siteName) => (
      <>
        <strong>{siteName}</strong> maneja únicamente ventas en línea con envío a domicilio, <em>¡no te dejes engañar!</em> Somos <strong>la mejor tienda de cigarros electrónicos en línea</strong>.
        <br /><br />
        Podemos darte <strong>asesoría personalizada</strong> para escoger el mejor producto para ti — escríbenos por WhatsApp de lunes a viernes de 10:00 a 17:00 hrs.
      </>
    ),
  },
  {
    id: 'faq-2',
    cat: 'envios',
    q: '¿Cuándo llegará mi pedido?',
    a: (siteName) => (
      <>
        Depende de la hora en que realizaste tu compra y de tu ciudad. Toda la información está en nuestra sección de envíos.
        <br /><br />
        Al finalizar tu pedido y pago, nuestro sistema te da una <strong>fecha estimada de entrega</strong> y por correo recibirás todos los avances hasta tener tu producto en tus manos.
      </>
    ),
  },
  {
    id: 'faq-3',
    cat: 'producto',
    q: '¿Qué más debo comprar para empezar a vapear, aparte de mi equipo?',
    a: () => (
      <>
        Si compras un <strong>equipo desechable</strong>, no necesitas nada más. Enciende y disfruta.
      </>
    ),
  },
  {
    id: 'faq-4',
    cat: 'producto',
    q: '¿Cuánto duran los vapes desechables?',
    a: () => (
      <>
        Un vape desechable de <strong>12,000 (12k) puffs</strong> dura en promedio <strong>entre 2 y 6 semanas</strong> (aproximadamente de 15 a 42 días), dependiendo por completo de tu frecuencia de uso.
        <br /><br />
        <ul className="space-y-1.5 text-white/70">
          <li><strong className="text-white/90">Uso ligero</strong> (pocas caladas al día): Puede durar de 3 a 4 semanas o un poco más.</li>
          <li><strong className="text-white/90">Uso moderado / regular</strong> (ritmo diario normal): Dura de 2 a 3 semanas.</li>
          <li><strong className="text-white/90">Uso intensivo</strong> (vapeo constante todo el día): Se puede agotar en 1 a 1.5 semanas.</li>
        </ul>
        <br />
        <strong>Referencia por capacidad:</strong>
        <ul className="mt-2 space-y-1 text-white/70">
          <li><strong className="text-white/90">12,000 puffs (12k):</strong> Entre 2 y 6 semanas según tu uso.</li>
          <li><strong className="text-white/90">13,000 puffs (13k):</strong> Aproximadamente entre 2.5 y 7 semanas, ideal si vapeas moderado o ligero.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq-5',
    cat: 'garantia',
    q: 'Mi vapeador requiere garantía, ¿dónde lo veo?',
    a: (siteName) => (
      <>
        En <strong>{siteName}</strong> puedes estar tranquilo: todos nuestros productos tienen <strong>garantía contra defectos de fabricación</strong>.
        <br /><br />
        Contáctanos por WhatsApp con tu número de pedido y una foto/video del defecto y te guiaremos en el proceso para darte el mejor servicio posible.
      </>
    ),
  },
];

export default function FAQPage() {
  const brand = useSiteBrand();
  const siteName = brand?.site_name || 'NubesMTY';
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      const inCat = cat === 'all' || f.cat === cat;
      if (!inCat) return false;
      if (!q) return true;
      return f.q.toLowerCase().includes(q);
    });
  }, [query, cat]);

  return (
    <div className="min-h-[calc(100vh-160px)] pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300/90">
              Centro de ayuda
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
            Preguntas <span className="text-orange-400">frecuentes</span>
          </h1>
          <p className="mt-4 text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Respuestas rápidas a las dudas más comunes sobre tus vapes, envíos y garantías en {siteName}.
          </p>

          {/* Search */}
          <div className="relative mt-8 max-w-xl mx-auto">
            <Search className="w-5 h-5 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Busca una pregunta… ej: resistencias, envío, garantía"
              className="h-14 pl-12 pr-4 rounded-full bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50 text-base"
              data-testid="faq-search-input"
            />
          </div>

          {/* Category pills */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {CATEGORIES.map((c) => {
              const Ic = c.icon;
              const active = cat === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold border transition-all ${
                    active
                      ? 'bg-orange-500 text-black border-orange-500 shadow-md shadow-orange-500/30'
                      : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`faq-cat-${c.id}`}
                >
                  <Ic className="w-3.5 h-3.5" />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto">
              <HelpCircle className="w-7 h-7 text-white/40" />
            </div>
            <p className="mt-4 text-white/60">No encontramos preguntas que coincidan. Prueba otra búsqueda o contáctanos abajo.</p>
          </div>
        ) : (
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {filtered.map((f, idx) => (
              <AccordionItem
                key={f.id}
                value={f.id}
                className="group rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent hover:border-orange-500/30 transition-colors overflow-hidden"
                data-testid={`faq-item-${f.id}`}
              >
                <AccordionTrigger className="px-5 md:px-6 py-5 text-left hover:no-underline [&[data-state=open]]:text-orange-400 [&[data-state=open]>svg]:rotate-180">
                  <div className="flex items-start gap-4 flex-1 pr-4">
                    <span className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-orange-500/10 border border-orange-500/25 text-orange-400 flex items-center justify-center font-black text-xs">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="text-white font-bold text-[15px] md:text-base leading-snug flex-1">
                      {f.q}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 md:px-6 pb-6 pt-0 -mt-1 text-white/70 text-[15px] leading-relaxed">
                  <div className="pl-12 md:pl-12">
                    {f.a(siteName)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </section>

      {/* Contact CTA */}
      <section className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#25D366]/10 to-transparent p-6 md:p-8 hover:border-[#25D366]/40 transition-colors">
            <div className="w-12 h-12 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-black text-white">¿Quieres atención más rápida?</h3>
            <p className="mt-2 text-white/60 text-sm">Contáctanos por WhatsApp y te respondemos en minutos.</p>
            <a
              href={`https://wa.me/${(brand?.whatsapp_number || brand?.whatsapp || '5218139147429').replace(/\D/g, '')}`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-black font-bold text-sm transition-colors"
              data-testid="faq-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> Escríbenos por WhatsApp
            </a>
          </div>

          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-orange-500/10 to-transparent p-6 md:p-8 hover:border-orange-500/40 transition-colors">
            <div className="w-12 h-12 rounded-full bg-orange-500/15 text-orange-400 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-black text-white">¿No encuentras lo que buscas?</h3>
            <p className="mt-2 text-white/60 text-sm">Mándanos un correo con tu situación (incluye tu número de pedido si lo tienes) y te respondemos en menos de 24h.</p>
            <a
              href={`mailto:${brand?.contact_email || brand?.support_email || 'contacto@nubesmty.com'}`}
              className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm transition-colors"
              data-testid="faq-email-btn"
            >
              <Mail className="w-4 h-4" /> Enviar correo
            </a>
          </div>
        </div>

        <p className="text-center mt-10 text-white/40 text-xs">
          <Zap className="w-3.5 h-3.5 inline mr-1 text-orange-400" />
          Actualizamos esta página con nuevas dudas cada mes. ¿Falta la tuya? Escríbenos.
        </p>
      </section>
    </div>
  );
}
